import { defineComponent as _defineComponent } from 'vue'
import BaseTable from "@/components-UI/table/BaseTable.vue";
import BasePopup from "@/components-UI/BasePopup.vue";
import BaseTooltip from "@/components-UI/BaseTooltip.vue";
import { useHoldConversions } from "@/services/TablePage/hold-conversions";
import { IHoldConversionReport } from "@crm-ui/types";
import { getHoldConversionsBff } from "@/api/affiliate-balance";
import { FormattedItem } from "@/utils/CellsFromBff";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router/composables";
import { downloadExcelFile, showServerError } from "@/utils";
import { useColumnsStore } from "@/store_v3/ColumnsStore";
import { useHoldSingleStore } from "@/store_v3/HoldSingleStore";
import { storeToRefs } from "pinia";
import isEqual from "lodash-es/isEqual";
import DateCell from "@/components-UI/table/cells/DateCell.vue";
import StatusCell from "@/components-UI/table/cells/StatusCell.vue";
import type { IStatus } from "@/types";
import type { StatusCellData } from "@/utils/CellsFactory";
import { getRemainingTime } from "@/utils/formatDates";


export default /*#__PURE__*/_defineComponent({
  __name: 'HoldConversionsTable',
  setup(__props, { expose }) {

const HOLD_CONVERSION_EVENT_TYPE_MAP: Record<string, IStatus> = {
    hold_expired: { text: 'Зачислено в баланс', color: 'green' },
    new_approved: { text: 'Новая принятая', color: 'blue' },
    old_approved: { text: 'Старая принятая', color: 'orange' },
    rejected: { text: 'Корректировка', color: 'purple' },
};

const translateHoldEventType = (status: string | boolean): IStatus => {
    const key = String(status);
    return HOLD_CONVERSION_EVENT_TYPE_MAP[key] ?? { text: key, color: 'brown' };
};

const holdEventTypeCellData: StatusCellData = {
    translate: translateHoldEventType,
};

const { headers, getDefaultColumns } = useHoldConversions();
const route = useRoute();
const { selected: columnsSelected } = storeToRefs(useColumnsStore());
const { filters: holdSingleFilters } = storeToRefs(useHoldSingleStore());
const { updateTotals } = useHoldSingleStore();

const isLoading = ref(true);
const items = ref<FormattedItem<IHoldConversionReport>[]>([]);

let loadListDebounceTimer: ReturnType<typeof setTimeout> | null = null;
/** Сигнатура последнего запроса — чтобы схлопывать дубли при множественных триггерах на маунте */
let lastRequestKey: string | null = null;

const resolveColumns = (): string[] => {
    const q = route.query.columns;
    if (columnsSelected.value?.length) {
        return columnsSelected.value;
    }
    if (Array.isArray(q)) {
        const list = q.filter(Boolean) as string[];
        if (list.length) return list;
    } else if (typeof q === "string" && q.length) {
        return q.split(",").map((s) => s.trim()).filter(Boolean);
    }
    return getDefaultColumns.value;
};

const getHeaders = computed(() => {
    const columns = resolveColumns();
    if (!columns.length) return headers;
    return headers.filter((h) => columns.includes(h.value));
});

watch(() => route.query.columns, (newVal, oldVal) => {
    if (isEqual(newVal, oldVal)) return;
    getList();
});

watch(holdSingleFilters, () => {
    getList();
}, { deep: true });

onMounted(() => {
    nextTick(() => {
        getList();
    });
});

onBeforeUnmount(() => {
    if (loadListDebounceTimer !== null) {
        clearTimeout(loadListDebounceTimer);
    }
});

const runGetList = async (): Promise<void> => {
    const params = getParams();
    const key = JSON.stringify(params);
    if (key === lastRequestKey) return;
    lastRequestKey = key;
    try {
        isLoading.value = true;
        const { data } = await getHoldConversionsBff(params);
        items.value = data.report;
        updateTotals(data.summary);
    } catch (err) {
        showServerError(err, "Список конверсий не загружен");
    } finally {
        isLoading.value = false;
    }
};

const exportCsv = async (): Promise<void> => {
    try {
        const params = getParams(true);
        const file = await getHoldConversionsBff(params, true);
        downloadExcelFile(file, 'Hold_conversions', 'csv', true);
    } catch (err) {
        showServerError(err as never, "Не удалось выгрузить CSV");
    }
};

const getList = (): void => {
    if (loadListDebounceTimer !== null) {
        clearTimeout(loadListDebounceTimer);
    }
    loadListDebounceTimer = setTimeout(() => {
        loadListDebounceTimer = null;
        void runGetList();
    }, 0);
};

const getParams = (isExport = false) => {
    const columns = resolveColumns();
    const filters = { ...holdSingleFilters.value };
    if (isExport) return { filters, columns };
    const paging = { limit: 100, offset: 0 };
    return { filters, columns, paging };
};

const openEventsKey = ref<string | null>(null);

const getEventsRowKey = (item: Partial<IHoldConversionReport>): string =>
    `${item.conversion_id ?? ''}-${item.transaction_id ?? ''}`;

const hasEvents = (item: Partial<IHoldConversionReport>): boolean =>
    Array.isArray(item.events) && item.events.length > 0;

const isEventsPanelOpen = (item: Partial<IHoldConversionReport>): boolean =>
    openEventsKey.value === getEventsRowKey(item);

const onEventsPopupUpdate = (item: Partial<IHoldConversionReport>, open: boolean): void => {
    const key = getEventsRowKey(item);
    if (open) {
        openEventsKey.value = key;
    } else if (openEventsKey.value === key) {
        openEventsKey.value = null;
    }
};

const resolveCurrency = (item: Partial<IHoldConversionReport>): string =>
    item.offer_currency ?? String(holdSingleFilters.value?.offer_currency ?? '');

const formatEventPayout = (payout: number, item: Partial<IHoldConversionReport>): string => {
    const cur = resolveCurrency(item);
    const formatted = Number(payout).toLocaleString('ru-RU', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
    });
    return cur ? `${formatted} ${cur}` : formatted;
};

expose({ exportCsv });

return { __sfc: true,HOLD_CONVERSION_EVENT_TYPE_MAP, translateHoldEventType, holdEventTypeCellData, headers, getDefaultColumns, route, columnsSelected, holdSingleFilters, updateTotals, isLoading, items, loadListDebounceTimer, lastRequestKey, resolveColumns, getHeaders, runGetList, exportCsv, getList, getParams, openEventsKey, getEventsRowKey, hasEvents, isEventsPanelOpen, onEventsPopupUpdate, resolveCurrency, formatEventPayout, BaseTable, BasePopup, BaseTooltip, DateCell, StatusCell, getRemainingTime }
}

})